import Minze, { Attrs, EventListeners, MinzeElement, Reactive } from 'minze'
import { v4 as uuid } from 'uuid'
import {
  FlowCore,
  IFlowContainer,
  IFlowCore,
  IFlowCoreConfig,
  IFlowTransition,
} from '../../../modules/epflow'
import { Locale } from '../../../modules/locale'
import MainModule from '../../../modules/main'
import { recursiveCleanObject } from '../../../modules/utils'
import { transformFlowDataToContributionFormat } from './utils'

Locale.getInstance().registerKeySet('fr', 'lvcCreateEventContainer', {
  eventCreationFailed: "Impossible de créer l'événement. Veuillez réessayer plus tard.",
})

Locale.getInstance().registerKeySet('en', 'lvcCreateEventContainer', {
  eventCreationFailed: 'Failed to create event. Please try again later.',
})

Locale.getInstance().registerKeySet('fr', 'lvcCreateContributionContainer', {
  contributionCreationFailed: 'Impossible de créer la contribution. Veuillez réessayer plus tard.',
})

Locale.getInstance().registerKeySet('en', 'lvcCreateContributionContainer', {
  contributionCreationFailed: 'Failed to create contribution. Please try again later.',
})

export interface LvcCreateContributionContainer {}

const STEPS = {
  CREATE_CONTRIBUTION_SEARCH_OR_ADD: 'createContributionSearchOrAdd',
  CREATE_CONTRIBUTION_SELECT_TYPE: 'createContributionSelectType',
  CREATE_CONTRIBUTION_ADD_EVENTS: 'createContributionAddEvents',
  CREATE_CONTRIBUTION_EVENT_PREVIEW: 'createContributionEventPreview',
  CREATE_CONTRIBUTION_RECAP: 'createContributionRecap',
  CREATE_CONTRIBUTION_SUBMITTED: 'createContributionSubmitted',
}

const CORE_CONFIG: IFlowCoreConfig = {
  initialStep: STEPS.CREATE_CONTRIBUTION_SEARCH_OR_ADD,
  eventType: 'contribution',
  calcProgress: (data: any, currentStep: IFlowTransition, transitions: IFlowTransition[]) => {
    let indexOf = transitions.indexOf(currentStep)

    return Math.round(100 * (indexOf / (transitions.length - 1)))
  },
  transitions: [
    {
      stepName: STEPS.CREATE_CONTRIBUTION_SEARCH_OR_ADD,
      elementName: (contextData: any) => `

      <lvc-create-contribution-search-or-add flow-step="${STEPS.CREATE_CONTRIBUTION_SEARCH_OR_ADD}" flow-group="${contextData.flowGroup}">
      </lvc-create-contribution-search-or-add>
  		`,
      transitionNext: async (data: any) => {
        return STEPS.CREATE_CONTRIBUTION_SELECT_TYPE
      },
      transitionPrev: async (data: any) => {
        return STEPS.CREATE_CONTRIBUTION_SEARCH_OR_ADD
      },
    },
    {
      stepName: STEPS.CREATE_CONTRIBUTION_SELECT_TYPE,
      elementName: (contextData: any) => `
      <lvc-create-contribution-select-type flow-step="${STEPS.CREATE_CONTRIBUTION_SELECT_TYPE}" flow-group="${contextData.flowGroup}">
      </lvc-create-contribution-select-type>
      `,
      transitionNext: async (data: any) => {
        return STEPS.CREATE_CONTRIBUTION_ADD_EVENTS
      },
      transitionPrev: async (data: any) => {
        return STEPS.CREATE_CONTRIBUTION_SEARCH_OR_ADD
      },
    },
    {
      stepName: STEPS.CREATE_CONTRIBUTION_ADD_EVENTS,
      elementName: (contextData: any) => `
      <lvc-create-contribution-add-events flow-step="${STEPS.CREATE_CONTRIBUTION_ADD_EVENTS}" flow-group="${contextData.flowGroup}">
      </lvc-create-contribution-add-events>
      `,
      transitionNext: async (data: any) => {
        return STEPS.CREATE_CONTRIBUTION_RECAP
      },
      transitionPrev: async (data: any) => {
        return STEPS.CREATE_CONTRIBUTION_SELECT_TYPE
      },
    },
    {
      stepName: STEPS.CREATE_CONTRIBUTION_EVENT_PREVIEW,
      elementName: (contextData: any) => `
      <lvc-create-contribution-event-preview flow-step="${STEPS.CREATE_CONTRIBUTION_EVENT_PREVIEW}" flow-group="${contextData.flowGroup}">
      </lvc-create-contribution-event-preview>
      `,
      transitionNext: async (data: any) => {
        return STEPS.CREATE_CONTRIBUTION_ADD_EVENTS
      },
      transitionPrev: async (data: any) => {
        return STEPS.CREATE_CONTRIBUTION_ADD_EVENTS
      },
    },
    {
      stepName: STEPS.CREATE_CONTRIBUTION_RECAP,
      elementName: (contextData: any) => `
      <lvc-create-contribution-recap flow-step="${STEPS.CREATE_CONTRIBUTION_RECAP}" flow-group="${contextData.flowGroup}">
      </lvc-create-contribution-recap>
      `,
      transitionNext: async (data: any) => {
        return STEPS.CREATE_CONTRIBUTION_SUBMITTED
      },
      transitionPrev: async (data: any) => {
        return STEPS.CREATE_CONTRIBUTION_ADD_EVENTS
      },
    },
    {
      stepName: STEPS.CREATE_CONTRIBUTION_SUBMITTED,
      elementName: (contextData: any) => `
      <lvc-create-contribution-submitted flow-step="${STEPS.CREATE_CONTRIBUTION_SUBMITTED}" flow-group="${contextData.flowGroup}">
      </lvc-create-contribution-submitted>
      `,
      transitionNext: async (data: any) => {
        return STEPS.CREATE_CONTRIBUTION_SUBMITTED
      },
      transitionPrev: async (data: any) => {
        return STEPS.CREATE_CONTRIBUTION_RECAP
      },
    },
  ] as IFlowTransition[],

  onSave: async (data: unknown, instance: IFlowCore): Promise<void> => {
    // Draft system disabled for contribution flow
  },

  onCommit: async (data: unknown, instance: IFlowCore): Promise<void> => {
    const mainModule = MainModule.getInstance()
    const flowCore = MainModule.getInstance().getFlowManager().getCore('contributionCreation')

    // Transform flow data to contribution format (numeric IDs)
    const contributionData = transformFlowDataToContributionFormat(data)

    // Call the backend to create the contribution
    const response = await mainModule
      .getAuth()
      .createContribution(flowCore?.entryId || uuid(), contributionData)

    if (!response.success) {
      // If unsuccessful, show error
      console.error('Error during contribution creation:', response.message)
      mainModule.showToastAlert(
        Locale.getKey('lvcCreateContributionContainer.contributionCreationFailed'),
        5000,
        true
      )
    } else {
      // If successful, proceed to next step
      flowCore?.next()
    }
  },
}

export class LvcCreateContributionContainer
  extends MinzeElement
  implements MinzeElement, IFlowContainer
{
  _authHandler: any = null
  flowGroup: string = 'contributionCreation'
  attrs: Attrs = []
  reactive: Reactive = ['stepName']
  flowCore: IFlowCore | null = null
  renderElement: string = ''
  draftId: string | null = null

  async onReady() {
    const mainModule = MainModule.getInstance()
    this._authHandler = this.handleAuthChanged.bind(this)
    mainModule.getEE().on('lvc:user_state_changed', this._authHandler)
    await this.handleAuthChanged({
      isLoggedIn: await mainModule.getAuth().isLoggedIn(),
    })
  }

  handleAuthChanged = async (data: any): Promise<void> => {
    if (!data.isLoggedIn) {
      console.warn('User is not logged in.')
      return
    }
    const mainModule = MainModule.getInstance()

    this.flowCore = new FlowCore({
      ...CORE_CONFIG,
      flowContainer: this,
    } as IFlowCoreConfig)
    mainModule.getFlowManager().putCore(this.flowCore)

    this.flowCore.entryId = uuid()

    // Initialize FlowCore (no draft support for contributions)
    await this.flowCore.initialize()

    // Notify footer navigation that the flow is ready.
    Minze.cast('lvc:flowcore-ready', this.flowCore)
  }

  async onContainerUpdate(data: any, step: IFlowTransition): Promise<void> {
    window.scrollTo(0, 0)
    let el: string = step?.elementName?.({ flowGroup: this.flowGroup }) || '<span>no element</span>'
    this.renderElement = el
    this.stepName = step.stepName
    this.rerender()
  }

  html = () => {
    let progress: number = this.flowCore?.getProgress() ?? 0
    let saveState = this.flowCore?.getSaveState() ?? 'not_saved'
    return `
		<div class="lvc-create-event-container">
		  <div class="lvc-content">
  			${this.renderElement}
			</div>
			<div class="lvc-footer-container">
				<lvc-footer-navigation
				  progress="${progress}"
				  flow-group="${this.flowGroup}"
				  step="${this.stepName}"
				  data-type="contribution"
				></lvc-footer-navigation>
			</div>
		</div>
		`
  }

  css = () => {
    return `
		.lvc-create-event-container {
			display: flex;
			flex-direction: column;
			min-height: 100vh;
			min-width: 100%;
			align-items: center;
			position: relative;
		}
		.lvc-content {
  		display: flex;
  		max-height: 100%;
  		flex-direction: column;
  		max-width: 600px;
      position: relative;
      padding-bottom: 200px;
      width: 100%;
		}
		.lvc-footer-container {
			display: flex;
			justify-content: center;
			flex-direction: column;
			margin-top: auto;
			width: 100%;
			position: fixed;
			bottom: 0;
		}`
  }

  eventListeners: EventListeners = []
}
